/* eslint-disable promise/always-return, promise/catch-or-return */
/**
 * @file MegaNav component
 */

import { debounce } from 'lodash-es';
import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import Manipulator from '../../utils/dom/manipulator';
import Modal from '../Modal';
import ScrollBarHelper from '../../utils/scrollbar-helper';
import Backdrop from '../../utils/backdrop';
import BaseComponent from '../BaseComponent';

const NAME = 'MegaNav';
const DATA_KEY = 'lta.meganav';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';

const ESCAPE_KEY = 'Escape';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;

const ITEM_EXP_CLASS = 'lta-mega-nav__item--expanded';
const CLASS_EXPANDED = 'lta-mega-nav--expanded';
const CLASS_ICON_BTN = 'is-active';
const CLASS_SEARCH_VISIBLE = 'lta-mega-nav__search--is-visible';
const CLASS_ADVANTAGE_VISIBLE = 'lta-mega-nav__advantage-menu--is-visible';
const CLASS_NAV_VISIBLE = 'lta-mega-nav__nav--is-visible';
const CLASS_MOBILE_LAYOUT = 'lta-mega-nav--mobile';
const CLASS_MOBILE_DESKTOP = 'lta-mega-nav--desktop';
const CLASS_NAME_BACKDROP = 'lta-mega-nav__offcanvas-backdrop';

const SELECTOR_DATA_TOGGLE = '[data-lta-meganav]';

class MegaNav extends BaseComponent {
	constructor(element) {
		super(element);

		this.isVisible = true;
		this.state = {
			currentMenuBtnEl: undefined,
			currentMenuId: undefined,
			currentMenuToggle: undefined,
			currentMenuPanelEl: undefined
		};

		// Default layout setting
		this.layout = 'mobile';
		// Check if subnav should open on hover
		this.hover = this._element.getAttribute('data-hover') && this._element.getAttribute('data-hover') == 'on';

		this.search = SelectorEngine.find('.js-lta-mega-nav__search', this._element);
		this.advantage = SelectorEngine.find('.js-lta-mega-nav__advantage-menu', this._element);
		this.searchCancelButton = SelectorEngine.find('.js-lta-mega-nav__search-cancel', this._element);
		this.searchActiveController = false;
		this.advantageActiveController = false;
		this.menu = SelectorEngine.find('.js-lta-mega-nav__nav', this._element);
		this.menuItems = SelectorEngine.find('.js-lta-mega-nav__item', this._element);
		this.subNavBackBtns = SelectorEngine.find('.js-lta-mega-nav__sub-nav-back-btn');
		this.multipleRolesAddedModalElement = document.querySelector('#multiple-roles-added')
		this._multipleRolesAddedModal = new Modal(this.multipleRolesAddedModalElement, {});
		this.addnewRoleBtn = SelectorEngine.find('.lta-add-new-role-btn', this._element);
		this.exitRoleModal = SelectorEngine.findOne('.js-exit-multiple-role-modal');
		this.megaNavControls = SelectorEngine.find('.js-lta-mega-nav__control .lta-mega-nav__control-icon-opener', this._element);
		this.megaNavLabels = SelectorEngine.find('.js-lta-mega-nav__control .lta-mega-nav__control-label', this._element);
		const megaNavControl = SelectorEngine.findOne('.mega-nav-controls');
		this.showModal = megaNavControl ? megaNavControl.getAttribute('data-show-modal') : false;
		this.redirectUrl = megaNavControl ? megaNavControl.getAttribute('data-url') : '';
		this.menuActiveController = false;

		this._backdrop = this._initializeBackDrop();

		if (this._element) {
			this.init();
		}

		this.expandedMenu = SelectorEngine.findOne('.lta-mega-nav__item--expanded', this._element);
		if (this.expandedMenu) {
			const triggerButton = SelectorEngine.findOne('.js-lta-mega-nav__control', this.expandedMenu);
			this.state.currentMenuBtnEl = triggerButton
		}

		const hamburgerEl = SelectorEngine.findOne('.lta-mega-nav__btn--hamburger', this._element);
		EventHandler.on(hamburgerEl, 'click', () => {
			const selectedMenu = SelectorEngine.findOne('[data-selected-menu-class]');
			if(selectedMenu) {
				const selectedMenuClass = Manipulator.getDataAttribute(selectedMenu, 'selectedMenuClass');
				selectedMenu.classList.add(selectedMenuClass);
			}
		});

		this.megaNavLabels.forEach(label => {
			EventHandler.on(label, 'click', (event) => {
				const mainItem = event.target.closest('.js-lta-mega-nav__control');
				if(mainItem.href) {
					window.location.href = mainItem.href;
				}
			});

			EventHandler.on(label, 'keydown', (event) => {
				if(['Enter', ' '].includes(event.key)) {
					const mainItem = event.target.closest('.js-lta-mega-nav__control');
					window.location.href = mainItem.href;
					event.preventDefault();
					event.stopPropagation();
				}
			});
		});

		this.megaNavControls.forEach(control => {
			const toggleFn = 
			EventHandler.on(control, 'keydown', (event) => {	
				const mainItem = event.target.closest('.js-lta-mega-nav__item');
				const anchor = event.target.closest('a');
				const itemExpanded = mainItem.classList.contains(ITEM_EXP_CLASS);						
				switch(event.key) {
					case ' ':
					case 'Enter':
						Manipulator.toggleClassCond(mainItem, ITEM_EXP_CLASS, !itemExpanded);
						this.toggleSubNavLabel(anchor, !itemExpanded);
						event.preventDefault();
						break;
					case 'ArrowDown':
						if(!itemExpanded) {
							Manipulator.toggleClassCond(mainItem, ITEM_EXP_CLASS, true);
							this.toggleSubNavLabel(anchor, true);
						}
						event.preventDefault();
						break;
					case 'ArrowUp':
						if(itemExpanded) {
							Manipulator.toggleClassCond(mainItem, ITEM_EXP_CLASS, false);
							this.toggleSubNavLabel(anchor, false);
						}
						event.preventDefault();
						break;					
					default:
						break;
				}
				event.stopPropagation();
			});
			EventHandler.on(control, 'click', (event) => {
				const mainItem = event.target.closest('.js-lta-mega-nav__item');
				const anchor = event.target.closest('a');
				const itemExpanded = mainItem.classList.contains(ITEM_EXP_CLASS);				
				Manipulator.toggleClassCond(mainItem, ITEM_EXP_CLASS, !itemExpanded);
				this.toggleSubNavLabel(anchor, !itemExpanded);
				event.preventDefault();
				event.stopPropagation();
			});
		});

		EventHandler.on(window, 'resize', () => {
			this.calculateNavbarHeight();
		});

		this.interval = setInterval(() => {
			this.calculateNavbarHeight();
		}, 500);

		setTimeout(() => {
			clearInterval(this.interval)
		}, 10000);
	}

	static get NAME() {
		return NAME;
	}

	calculateNavbarHeight() {
		const main = document.querySelector('body');
		const navBarHeight = this._element?.offsetHeight || 73;
		const mainPaddingTop = Number(main.style.paddingTop.split('px')?.[0]);
		if(mainPaddingTop !== navBarHeight) {
			main.style.paddingTop = `${navBarHeight}px`;
			this.menu[0].style.top = `${navBarHeight}px`;
			if(this.multipleRolesAddedModalElement?.style) {
				this.multipleRolesAddedModalElement.style.top = `${navBarHeight}px`;
			}
		}
	}

	init() {
		this.initMegaNav();

		// On window resize - update layout
		const customEvent = new CustomEvent('update-menu-layout');
		let previousScroll = 0;

		window.addEventListener(
			'resize',
			debounce(() => {
				this._element.dispatchEvent(customEvent);
			}, 200)
		);

		// Same offset, horizontal filter not visible in initial load so offset is computed wrong.
		const horizontalFilter = SelectorEngine.findOne('.js-lta-listview');
		let offsetTop;
		if (horizontalFilter) offsetTop = horizontalFilter.getBoundingClientRect().top;

		window.addEventListener('scroll', () => {
			const currentScroll = document.documentElement.scrollTop;
			document.body.classList.toggle('after-filters', currentScroll > offsetTop);

			if (currentScroll > 0 && currentScroll < document.body.clientHeight - window.outerHeight) {
				this.toggleNav(!(currentScroll > previousScroll));
				previousScroll = currentScroll;
			}
		});

		window.requestAnimationFrame(() => this._element.dispatchEvent(customEvent));

		this.addnewRoleBtn.forEach(btn => {
			EventHandler.on(btn, 'click', () => {
				if (this.showModal == 'true') {
					this._multipleRolesAddedModal.show();
				} else {
					window.location.href = this.redirectUrl;
				}
			});
		});

		EventHandler.on(this.exitRoleModal, 'click', () => {
			this._multipleRolesAddedModal.hide();
		});
	}

	initMegaNav() {
		this.setMegaNavLayout(); // Switch between mobile/desktop layout
		this.initSearch();
		this.initAdvantage();
		this.initMenu();
		this.initSubNav();

		this.expandedMenu = SelectorEngine.findOne(`.${ITEM_EXP_CLASS}`, this._element);
		if (this.expandedMenu) {
			const triggerButton = SelectorEngine.findOne('.js-lta-mega-nav__control', this.expandedMenu);
			this.state.currentMenuBtnEl = triggerButton
		}

		EventHandler.on(this._element, 'focusin', () => {
			if (!this.isVisible) {
				this.toggleNav(true);
			}
		});

		EventHandler.on(this._element, 'update-menu-layout', () => {
			this.setMegaNavLayout(); // On window resize - update layout
		});
	}

	toggleNav(shouldShow) {

		// this will activate only when the query parameter is passed from the mobile app.
		if (document.querySelector('body')) {
			let isLTAPLAYMOBILE = document.querySelector('body').getAttribute('data-is-LTAPLAYMOBILE');
			if (localStorage.getItem('isLTAPLAYMOBILE') == 'true') {
				isLTAPLAYMOBILE = 'True';
			}
			if (isLTAPLAYMOBILE !== null && isLTAPLAYMOBILE !== undefined && isLTAPLAYMOBILE == "True") {
				return;
			}
		}
		this.isVisible = shouldShow;
		document.body.classList.toggle('with-nav', shouldShow);
		document.body.classList.toggle('no-nav', !shouldShow);
		this._element.classList.toggle('lta-mega-nav--is-visible', shouldShow);
		this._element.classList.toggle('lta-mega-nav--is-hidden', !shouldShow);
	}

	setMegaNavLayout() {
		const layout = getComputedStyle(this._element).getPropertyValue('--lta-mega-nav-layout').trim();

		if (layout === this.layout) return;

		this.layout = layout;

		Manipulator.toggleClassCond(this._element, CLASS_MOBILE_DESKTOP, this.layout === 'desktop');
		Manipulator.toggleClassCond(this._element, CLASS_MOBILE_LAYOUT, this.layout !== 'desktop');

		this.resetMegaNavOffset();
		this.resetNavAppearance();
	}

	resetMegaNavOffset() {
		document.documentElement.style.setProperty('--lta-mega-nav-offset-y', this._element.getBoundingClientRect().top + 'px');
	}

	closeNavigation() {
		// Triggered by Esc key press
		// Close search
		this.closeSearch();
		this.closeAdvantage();
		// Close nav
		if (this.menu[0].classList.contains(CLASS_NAV_VISIBLE)) {
			this.toggleMenu(this.menu[0], 'menuActiveController', CLASS_NAV_VISIBLE, this.menuActiveController, true);
		}
		// Close subnav
		this.closeSubNav(false);
		this.resetNavAppearance(); // Reset nav expanded appearance
	}

	closeFocusNavigation() {
		// Triggered by Tab key pressed
		// Close search when focus is lost
		if (this.search[0].classList.contains(CLASS_SEARCH_VISIBLE) && !document.activeElement.closest('.js-lta-mega-nav__search')) {
			this.toggleMenu(this.search[0], 'searchActiveController', CLASS_SEARCH_VISIBLE, this.searchActiveController, true);
		}

		// Close nav when focus is lost
		if (this.menu[0].classList.contains(CLASS_NAV_VISIBLE) && !document.activeElement.closest('.js-lta-mega-nav__nav')) {
			this.toggleMenu(this.menu[0], 'menuActiveController', CLASS_NAV_VISIBLE, this.menuActiveController, true);
		}

		// Close subnav when focus is lost
		for (let i = 0; i < this.menuItems.length; i++) {
			if (!this.menuItems[i].classList.contains(ITEM_EXP_CLASS)) continue;

			const parentItem = document.activeElement.closest('.js-lta-mega-nav__item');

			if (parentItem && parentItem == this.menuItems[i]) continue;

			this.closeSingleSubnav(i);
		}

		this.resetNavAppearance(); // reset nav expanded appearance
	}

	closeSearch(bool) {
		if (this.search.length === 0) return;

		if (this.search[0].classList.contains(CLASS_SEARCH_VISIBLE)) {
			this.toggleMenu(this.search[0], 'searchActiveController', CLASS_SEARCH_VISIBLE, this.searchActiveController, bool);
		}
	}

	closeAdvantage(bool) {
		if (this.advantage.length === 0) return;

		if (this.advantage[0].classList.contains(CLASS_ADVANTAGE_VISIBLE)) {
			this.toggleMenu(this.advantage[0], 'advantageAtiveController', CLASS_ADVANTAGE_VISIBLE, this.advantageActiveController, bool);
		}
	}

	initAdvantage() {
		if (this.advantage.length === 0) return;

		// Toggle advantage menu
		this.advantageToggles = document.querySelectorAll('[aria-controls="' + this.advantage[0].getAttribute('id') + '"]');
		this.advantageToggles.forEach((advantageToggle) => {
			advantageToggle.addEventListener('click', (event) => {
				// Toggle search
				this.toggleMenu(this.advantage[0], 'advantageActiveController', CLASS_ADVANTAGE_VISIBLE, advantageToggle, true);
				// Close nav if it was open
				if (this.menu[0].classList.contains(CLASS_NAV_VISIBLE)) {
					this.toggleMenu(this.menu[0], 'menuActiveController', CLASS_NAV_VISIBLE, this.menuActiveController, false);
				}
				// close subnavigation if open
				this.closeSubNav(false);
				this.closeSearch(false);
				this.resetNavAppearance(); // reset nav expanded appearance
			});
		});

		EventHandler.on(this.advantage[0], 'keyup', (event) => {
			if (event.key === ESCAPE_KEY) {
				event.preventDefault();
				this.closeSubNav(false);
				this.closeAdvantage();
				this.resetNavAppearance(); // reset nav expanded appearance
				new ScrollBarHelper().reset();
			}
		});
	}

	initSearch() {
		if (this.search.length === 0) return;

		// Toggle search
		this.searchToggles = document.querySelectorAll('[aria-controls="' + this.search[0].getAttribute('id') + '"]');

		EventHandler.on(this.searchCancelButton[0], 'click', (event) => { 
			event.preventDefault(); 
			this.closeSearch();
		});

		EventHandler.on(this.search[0], 'keyup', (event) => {
			if (event.key === ESCAPE_KEY) {
				event.preventDefault();
				this.search[0].classList.remove('lta-mega-nav__search--is-visible');
				if (this.menu[0].classList.contains(CLASS_NAV_VISIBLE)) {
					this.toggleMenu(this.menu[0], 'menuActiveController', CLASS_NAV_VISIBLE, this.menuActiveController, false);
				}
				this.closeSubNav(false);
				this.resetNavAppearance(); // reset nav expanded appearance
				this._backdrop.hide();
				new ScrollBarHelper().reset();
			}
		});

		this.searchToggles.forEach((searchToggle) => {
			searchToggle.addEventListener('click', (event) => {
				// Toggle search
				this.toggleMenu(this.search[0], 'searchActiveController', CLASS_SEARCH_VISIBLE, searchToggle, true);
				// Close nav if it was open
				if (this.menu[0].classList.contains(CLASS_NAV_VISIBLE)) {
					this.toggleMenu(this.menu[0], 'menuActiveController', CLASS_NAV_VISIBLE, this.menuActiveController, false);
				}
				// close subnavigation if open
				this.closeSubNav(false);
				this.closeAdvantage(false);
				this.resetNavAppearance(); // reset nav expanded appearance
			});
		});
	}

	initMenu() {
		if (this.menu.length === 0) return;

		// Toggle nav / hamburger menu button
		this.menuToggles = document.querySelectorAll('[aria-controls="' + this.menu[0].getAttribute('id') + '"]');

		this.menuToggles.forEach((menuToggle) => {
			menuToggle.addEventListener('click', (event) => {
				// Toggle nav
				this.toggleMenu(this.menu[0], 'menuActiveController', CLASS_NAV_VISIBLE, menuToggle, true);

				// Close search if it was open
				if (this.search[0].classList.contains(CLASS_SEARCH_VISIBLE)) {
					this.toggleMenu(this.search[0], 'searchActiveController', CLASS_SEARCH_VISIBLE, this.searchActiveController, false);
				}

				// Close advantage if it was open
				if (this.advantage[0].classList.contains(CLASS_ADVANTAGE_VISIBLE)) {
					this.toggleMenu(this.advantage[0], 'advantageActiveController', CLASS_ADVANTAGE_VISIBLE, this.advantageActiveController, false);
				}

				this.resetNavAppearance(); // Reset nav expanded appearance
				this.state.currentMenuToggle = menuToggle;
			});

			EventHandler.on(this.menu[0], 'keyup', (event) => {
				if (event.key === ESCAPE_KEY) {
					event.preventDefault();
					this.closeSubNav(false);
					this.resetNavAppearance(); // reset nav expanded appearance
					new ScrollBarHelper().reset();
				}
			});

		});
	}

	toggleMenu(element, controller, visibleClass, toggle, moveFocus) {
		const menuIsVisible = element.classList.contains(visibleClass);

		Manipulator.toggleClassCond(element, visibleClass, !menuIsVisible);
		Manipulator.toggleClassCond(toggle, CLASS_ICON_BTN, !menuIsVisible);
		menuIsVisible ? toggle.removeAttribute('aria-expanded') : toggle.setAttribute('aria-expanded', 'true');

		if (menuIsVisible) {
			if (toggle && moveFocus) toggle.focus();
			this[controller] = false;
		} else {
			if (toggle) this[controller] = toggle;

			const firstFocusableEl = this.getFirstFocusable(element);
			if (firstFocusableEl) {
				firstFocusableEl.focus(); // move focus to first focusable element
			}
		}

		if (!menuIsVisible) {
			this._backdrop.show();
			new ScrollBarHelper().hide();
		} else {
			this._backdrop.hide();
			new ScrollBarHelper().reset();
		}
	}

	getFirstFocusable(element) {
		// eslint-disable-next-line max-len
		const focusableEle = element.querySelectorAll(
			'[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable], audio[controls], video[controls], summary'
		);
		let firstFocusable = false;

		for (let i = 0; i < focusableEle.length; i++) {
			if (focusableEle[i].offsetWidth || focusableEle[i].offsetHeight || focusableEle[i].getClientRects().length > 0) {
				firstFocusable = focusableEle[i];
				break;
			}
		}

		return firstFocusable;
	}

	initSubNav() {
		// Toggle subnavigation visibility
		this._element.addEventListener('click', (event) => {
			this.toggleSubNav(event, 'click');
		});

		this.subNavBackBtns.forEach((backBtnEl) => {
			backBtnEl.addEventListener('click', (event) => {
				this.toggleSubNav(event, 'click', 'subnav');
			});
		});

		if (this.hover) {
			// data-hover="on" => use mouse events
			this._element.addEventListener('mouseover', (event) => {
				if (this.layout != 'desktop') return;
				this.toggleSubNav(event, 'mouseover');
			});

			this._element.addEventListener('mouseout', (event) => {
				if (this.layout != 'desktop') return;

				const mainItem = event.target.closest('.js-lta-mega-nav__item');
				if (!mainItem) return;
				const triggerBtn = mainItem.getElementsByClassName('js-lta-mega-nav__control');
				if (triggerBtn.length === 0) return;
				const itemExpanded = mainItem.classList.contains(ITEM_EXP_CLASS);
				if (!itemExpanded) return;
				const mainItemHover = event.relatedTarget;
				if (mainItemHover && mainItem.contains(mainItemHover)) return;

				Manipulator.toggleClassCond(mainItem, ITEM_EXP_CLASS, !itemExpanded);
				itemExpanded ? triggerBtn[0].removeAttribute('aria-expanded') : triggerBtn[0].setAttribute('aria-expanded', 'true');
				itemExpanded ? this._backdrop.hide(): '';
			});
		}
	}

	toggleSubNav(event, eventType, source = 'nav') {
		const triggerBtn =
			source === 'nav' ? event.target.closest('.js-lta-mega-nav__control') : this.state.currentMenuBtnEl.closest('.js-lta-mega-nav__control');
		if (!triggerBtn) return;

		this.state.currentMenuBtnEl = triggerBtn;

		const mainItem = triggerBtn.closest('.js-lta-mega-nav__item');
		if (!mainItem) return;

		const itemExpanded = mainItem.classList.contains(ITEM_EXP_CLASS);
		if (this.hover && itemExpanded && this.layout === 'desktop' && eventType !== 'click') return;
		this._backdrop.show();

		Manipulator.toggleClassCond(mainItem, ITEM_EXP_CLASS, !itemExpanded);
		itemExpanded ? triggerBtn.removeAttribute('aria-expanded') : triggerBtn.setAttribute('aria-expanded', 'true');

		if (this.layout == 'desktop' && !itemExpanded) this.closeSubNav(mainItem);
		// Close search if open
		this.closeSearch(false);
		this.closeAdvantage(false);
		this.resetNavAppearance(); // reset nav expanded appearance
	}

	closeSubNav(selectedItem) {
		// Close subnav when a new sub nav element is open
		if (this.menuItems.length === 0) return;

		for (let i = 0; i < this.menuItems.length; i++) {
			if (this.menuItems[i] !== selectedItem) this.closeSingleSubnav(i);
		}
	}

	closeSingleSubnav(index) {
		this.menuItems[index].classList.remove(ITEM_EXP_CLASS);
		const triggerBtn = SelectorEngine.find('.js-lta-mega-nav__control', this.menuItems[index]);
		if (triggerBtn.length > 0) triggerBtn[0].removeAttribute('aria-expanded');
	}

	resetNavAppearance() {
		(this._element.getElementsByClassName(ITEM_EXP_CLASS).length > 0 && this.layout == 'desktop') ||
		this._element.getElementsByClassName(CLASS_SEARCH_VISIBLE).length > 0 ||
		(this._element.getElementsByClassName(CLASS_NAV_VISIBLE).length > 0 && this.layout == 'mobile')
			? this._element.classList.add(CLASS_EXPANDED)
			: this._element.classList.remove(CLASS_EXPANDED);
	}

	toggleSubNavLabel(label, bool) {
		label.setAttribute('aria-expanded', bool);
	}

	_initializeBackDrop() {
		return new Backdrop({
			className: CLASS_NAME_BACKDROP,
			isVisible: true,
			isAnimated: true,
			rootElement: this._element.parentNode,
			// This could be use `this.toggleMenu(this.menu[0], 'menuActiveController', CLASS_NAV_VISIBLE, this.state.currentMenuToggle, true)`
			// to keep the state of the opened menus. Not sure if it will be ever needed.
			clickCallback: () => this.closeNavigation()
		});
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();
	MegaNav.getOrCreateInstance(this);
});

export default MegaNav;
/* eslint-enable promise/always-return, promise/catch-or-return */
